import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { addSchema, AddModelFormValues } from "./validations";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { routes } from "@/routes/utils";
import { toast } from "sonner";
import {
  useAddAnnualReportingImageMutation,
  useAddAnnualReportingMutation,
  useUpdateAnnualReportingMutation,
} from "@/services";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRef } from "react";
import { NAME, UPLOAD } from "./consts";
import { useLocation } from "react-router-dom";

const useAddEdit = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isView = action == "view";
  const isEdit = action == "edit";
  const isMobile = useIsMobile();
  const uploadRef = useRef<any>(null);

  const form = useForm<AddModelFormValues>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      [NAME]: "",
      [UPLOAD]: "",
    },
  });

  const location = useLocation();
  const applicableCompany = location.state?.applicableCompany;

  const requiredFields = getRequiredFields(addSchema);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.annualReporting}`);
  };

  const { mutate: addInfo, isPending: isAddPending } =
    useAddAnnualReportingMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Annual Reporting Added Successfully", {
          className: "!bg-green-600 !text-white",
        });
        queryClient.invalidateQueries({
          queryKey: ["annual-reporting"],
        });
        onBackClick();
      },
    });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateAnnualReportingMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Annual Reporting Updated Successfully", {
          className: "!bg-green-600 !text-white",
        });
        queryClient.invalidateQueries({
          queryKey: ["annual-reporting"],
        });
        onBackClick();
      },
    });

  const { mutate: addImageInfo, isPending: isAddImagePending } =
    useAddAnnualReportingImageMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.issues?.error === "string"
            ? error?.response?.data?.issues?.error
            : typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return error?.response?.data?.message;
      },
      onSuccess: (data: any) => {
        const values = form.watch();

        if (id) {
          updateInfo({
            id,
            data: {
              ...values,
              [UPLOAD]: data?.data?.data?.attachmentId,
            },
          });
        } else {
          addInfo({
            data: {
              ...values,
              [UPLOAD]: data?.data?.data?.attachmentId,
              applicableCompany: applicableCompany === "MBAPL" ? "MBAPL" : "OSTWAL",
            },
          });
        }
      },
    });

  const onSubmit = (data: AddModelFormValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(data);

    if (!_data[UPLOAD]) {
      form.setError(UPLOAD, {
        message: "This Field is required",
      });
      return;
    }

    addImageInfo({ data: { file: _data[UPLOAD] } });
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isAddPending,
    isMobile,
    uploadRef,
    isAddImagePending,
    action,
    isUpdatePending,
    isEdit,
  };
};

export default useAddEdit;
